// AccountSettings.jsx — self-service password/email change, opened from the
// NavUserProfile flyout in Sidebar.jsx.
import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { apiFetchJson } from "../apiClient";
import API_BASE_URL from "../config";
import { DATABASES } from "../utils/databases";
import { MANAGED_FIELDS } from "../utils/identity";
import IdentityGroups from "./IdentityGroups";
import ModalPortal from "./ModalPortal";
import "./AccountSettings.css";

function PasswordField({ value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="acct-password-wrapper">
      <input
        type={show ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
      />
      <button
        type="button"
        className="acct-password-toggle"
        onClick={() => setShow((v) => !v)}
        tabIndex={-1}
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

function ChangePasswordForm() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState(null); // { type: "ok" | "error", text }
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: "New passwords do not match" });
      return;
    }
    setSubmitting(true);
    try {
      await apiFetchJson("/api/v1/auth/me/password", {
        method: "PATCH",
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      setMessage({ type: "ok", text: "Password changed. Other signed-in devices have been logged out." });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Failed to change password" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="acct-form">
      <h4>Change Password</h4>
      {message && (
        <p className={message.type === "error" ? "acct-error" : "acct-success"}>{message.text}</p>
      )}
      <PasswordField value={currentPassword} onChange={setCurrentPassword} placeholder="Current password" />
      <PasswordField value={newPassword} onChange={setNewPassword} placeholder="New password" />
      <PasswordField value={confirmPassword} onChange={setConfirmPassword} placeholder="Confirm new password" />
      <button type="submit" className="acct-submit-btn" disabled={submitting}>
        {submitting ? "Saving…" : "Change Password"}
      </button>
    </form>
  );
}

function ChangeEmailForm({ currentEmail }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [message, setMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setSubmitting(true);
    try {
      await apiFetchJson("/api/v1/auth/me/email", {
        method: "PATCH",
        body: JSON.stringify({ current_password: currentPassword, new_email: newEmail }),
      });
      setMessage({ type: "ok", text: `Email changed to ${newEmail}.` });
      setCurrentPassword("");
      setNewEmail("");
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Failed to change email" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="acct-form">
      <h4>Change Email</h4>
      <p className="acct-current-value">Current: {currentEmail}</p>
      {message && (
        <p className={message.type === "error" ? "acct-error" : "acct-success"}>{message.text}</p>
      )}
      <input
        type="email"
        placeholder="New email"
        value={newEmail}
        onChange={(e) => setNewEmail(e.target.value)}
        required
      />
      <PasswordField value={currentPassword} onChange={setCurrentPassword} placeholder="Current password" />
      <button type="submit" className="acct-submit-btn" disabled={submitting}>
        {submitting ? "Saving…" : "Change Email"}
      </button>
    </form>
  );
}

// Feature 9 — "export and exit": one ZIP with the caller's visible documents,
// conversation history and records for the chosen workspace. A plain link, so
// the browser streams the download with the session cookie.
export function ExportDataSection({ defaultWorkspace }) {
  const [workspace, setWorkspace] = useState(defaultWorkspace || DATABASES[0]);
  const href =
    `${API_BASE_URL}/api/v1/export/archive?database=${encodeURIComponent(workspace)}`;
  return (
    <div className="acct-form">
      <h4>Export my data</h4>
      <p className="acct-current-value">
        Documents you may access, your conversations and calculation records as one ZIP.
        Documents under a licence you do not hold are listed but not included.
      </p>
      <select
        aria-label="Workspace to export"
        value={workspace}
        onChange={(e) => setWorkspace(e.target.value)}
      >
        {DATABASES.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
      <a className="acct-submit-btn" href={href} download>Download export</a>
    </div>
  );
}

// Label key and display value of each IdP-manageable field of /auth/me.
const MANAGED_FIELD_VIEW = {
  [MANAGED_FIELDS.role]: { label: "identity.fieldRole", value: (u, t) => u.role || t("identity.none") },
  [MANAGED_FIELDS.database]: { label: "identity.fieldDatabase", value: (u, t) => u.database || t("identity.allWorkspaces") },
  [MANAGED_FIELDS.licences]: {
    label: "identity.fieldLicences",
    value: (u, t) => ((u.licences || []).length ? u.licences.join(", ") : t("identity.none")),
  },
};

/** An SSO account has no password here: its credentials, and possibly its role /
 * workspace / licences (`managed_fields`), belong to the organisation's IdP. */
export function ManagedIdentitySection({ user }) {
  const { t } = useTranslation();
  const managed = (user.managed_fields || []).filter((f) => MANAGED_FIELD_VIEW[f]);
  return (
    <div className="acct-form">
      <p className="acct-current-value">{t("identity.accountManaged")}</p>
      <h4>{t("identity.groups")}</h4>
      <IdentityGroups groups={user.groups} />
      {managed.length > 0 && (
        <>
          <h4>{t("identity.managedFields")}</h4>
          <dl className="acct-managed-fields">
            {managed.map((field) => (
              <div key={field} className="acct-managed-field">
                <dt>{t(MANAGED_FIELD_VIEW[field].label)}</dt>
                <dd>{MANAGED_FIELD_VIEW[field].value(user, t)}</dd>
              </div>
            ))}
          </dl>
        </>
      )}
    </div>
  );
}

export default function AccountSettings({ user, onClose }) {
  // Only an explicit `false` hides the forms, so an older /auth/me without the
  // field keeps today's behaviour.
  const passwordless = user?.has_password === false;
  return (
    <ModalPortal>
        <div className="acct-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="acct-modal" onClick={(e) => e.stopPropagation()}>
        <div className="acct-modal-header">
          <span>Account Settings</span>
          <button type="button" className="acct-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {passwordless ? (
          <ManagedIdentitySection user={user} />
        ) : (
          <>
            <ChangePasswordForm />
            <div className="acct-divider" />
            <ChangeEmailForm currentEmail={user?.email || ""} />
          </>
        )}
        <div className="acct-divider" />
        <ExportDataSection defaultWorkspace={user?.database || ""} />
      </div>
    </div>
    </ModalPortal>
  );
}
